import React from 'react';
import {useNavigate} from 'react-router-dom';
import {Box, List, ListItem, Typography, Stack} from '@mui/material';

import {useGetDocumentsSharedWithMeQuery} from '../../data/documentWorkspaceApi';
import Loading from '../../components/Loading';
import ArrowRightIcon from '../../assets/arrow-right.svg';
import {useSelector} from 'react-redux';
import {
  DARK_TEXT_COLOR,
  GREY_BORDER,
  GREY_TEXT_SECONDARY,
  WHITE_100,
} from '../../theme';
import {getColorByOrgHash, sharedStyles} from './ExternalUserLandingPage';

const styles = {
  greeting: {
    marginBottom: '2.25rem !important',
    color: DARK_TEXT_COLOR,
    fontSize: '32px',
    fontWeight: 500,
    lineHeight: '130%',
    letterSpacing: '-0.32px',
  },
  listItem: {
    maxWidth: '1268px',
    border: `1px solid ${GREY_BORDER}`,
    marginBottom: '0.875rem',
    borderRadius: '12px',
    cursor: 'pointer',
    padding: '1.5rem 2.5rem',
    backgroundColor: '#FFFFFF',
    '&:hover': {
      border: `1px solid #054A41`,
    },
  },
  companyName: {
    color: DARK_TEXT_COLOR,
    fontSize: '20px',
    fontWeight: 600,
    lineHeight: '150%',
    letterSpacing: '-0.2px',
  },
  initialIcon: {
    width: '52px',
    height: '52px',
    borderRadius: '50%',
    color: WHITE_100,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
    lineHeight: '130%',
    fontWeight: 500,
    marginRight: '1rem',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4rem 2rem',
    textAlign: 'center',
  },
  emptyStateTitle: {
    color: DARK_TEXT_COLOR,
    fontSize: '20px',
    fontWeight: 600,
    marginBottom: '0.5rem',
    lineHeight: '150%',
  },
  emptyStateSubtitle: {
    color: GREY_TEXT_SECONDARY,
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '140%',
  },
};

const ExternalUserHomePage = () => {
  const navigate = useNavigate();
  const {isExternalUser, currentUser, accessToken} = useSelector(
    (state) => state.session
  );

  // Wait for both isExternalUser AND accessToken to be set before querying
  // This prevents a race condition where the query fires before the async token fetch completes
  const {data: sharedWithMe, isFetching} = useGetDocumentsSharedWithMeQuery(
    undefined,
    {
      skip: !isExternalUser || !accessToken,
    }
  );

  // Only list organizations, not documents.
  const organizations = sharedWithMe?.recordsByOrg
    ? Object.values(sharedWithMe.recordsByOrg).map(
        (entry) => entry.organization
      )
    : [];

  const handleOrgClick = (orgId) => {
    navigate(`/shared/org/${orgId}`);
  };

  if (isFetching) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        sx={{height: '50vh', width: '100%'}}
      >
        <Loading loading={true} message="Loading organizations" />
      </Box>
    );
  }

  return (
    <>
      <div style={sharedStyles.stickyHeader}>
        <Stack spacing={1} sx={{width: '100%'}}>
          <Typography variant={'h4'} sx={styles.greeting}>
            {`Hi ${currentUser?.firstName || 'there'}, welcome back`}
          </Typography>
        </Stack>
      </div>
      <div style={sharedStyles.scrollableContent}>
        <List>
          {organizations.length === 0 ? (
            <Box sx={styles.emptyState}>
              <Typography variant="h6" sx={styles.emptyStateTitle}>
                No organizations found.
              </Typography>
              <Typography variant="body2" sx={styles.emptyStateSubtitle}>
                Organizations that share documents with you will appear here.
              </Typography>
            </Box>
          ) : (
            organizations.map(({orgId, orgName}) => (
              <ListItem
                key={orgId}
                sx={styles.listItem}
                onClick={() => handleOrgClick(orgId)}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{width: '100%'}}
                >
                  <div
                    style={{
                      ...styles.initialIcon,
                      backgroundColor: getColorByOrgHash(orgId),
                    }}
                  >
                    {orgName ? orgName.charAt(0).toUpperCase() : '?'}
                  </div>
                  <Stack sx={{flex: 2}} spacing={0.5}>
                    <Typography variant="caption" sx={styles.companyName}>
                      {orgName || 'Unset'}
                    </Typography>
                  </Stack>
                  <Stack
                    sx={{
                      flex: 0,
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginLeft: 'auto',
                    }}
                  >
                    <img
                      src={ArrowRightIcon}
                      height={24}
                      width={24}
                      alt="arrow-right"
                      style={{marginLeft: '2rem', marginRight: '-1.5rem'}}
                    />
                  </Stack>
                </Stack>
              </ListItem>
            ))
          )}
        </List>
      </div>
    </>
  );
};

export default ExternalUserHomePage;
